import * as React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useSession, useToastError } from '@hooks';
import { useUpdateUserMutation } from '@graphql';

export function Lastname() {
  const { user } = useSession();
  const { t } = useTranslation('cta');
  const [isEditing, setIsEditing] = React.useState(false);
  const toastError = useToastError();

  const [updateUser] = useUpdateUserMutation();

  const formik = useFormik({
    initialValues: { lastname: user?.lastname ?? '' },
    validationSchema: yup.object({
      lastname: yup.string(),
    }),
    onSubmit: (values) => {
      updateUser({
        variables: {
          input: values,
        },
        onCompleted() {
          formik.resetForm({ values: { lastname: values.lastname } });
          setIsEditing(false);
        },
        onError: toastError,
      });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (!user) {
    return null;
  }

  return (
    <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
      <Stack width="100%" spacing={isEditing ? 1 : 0}>
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography fontWeight="bold">{t('lastname.label', { ns: 'field' })}</Typography>
          <Stack spacing={1} direction="row">
            {isEditing ? (
              <>
                <Button variant="contained" color="uncolored" size="small" onClick={handleCancel}>
                  {t('cancel')}
                </Button>
                <Button
                  disabled={!formik.dirty || !formik.isValid}
                  variant="contained"
                  color="success"
                  size="small"
                  type="submit"
                >
                  {t('validate')}
                </Button>
              </>
            ) : (
              <Button variant="contained" color="uncolored" size="small" onClick={handleEdit}>
                {t('edit')}
              </Button>
            )}
          </Stack>
        </Stack>
        <Stack>
          {isEditing ? (
            <CssTextField
              required
              fullWidth
              size="small"
              type="lastname"
              name="lastname"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              error={formik.touched.lastname && Boolean(formik.errors.lastname)}
              helperText={formik.touched.lastname && formik.errors.lastname}
            ></CssTextField>
          ) : (
            <Typography color="text.secondary">{user.lastname}</Typography>
          )}
        </Stack>
      </Stack>
    </form>
  );
}

const CssTextField = styled(TextField)(({ theme }) => ({
  '& label.Mui-focused': {
    color: '#999999',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#B2BAC2',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#434343',
    },
    '&:hover fieldset': {
      borderColor: '#666666',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#999999',
    },
  },
}));
